/**
 * Shared KPI-style table chrome for Bandwidth — aligned with KPI Dashboard (steel blue headers, indigo accents).
 * @see kpi-dashboard KpiIncidentDashboardTab / KpiCiHeatmapTable header blues
 */

/** Table header row (<tr>) — matches KPI incident / heatmap steel blue */
export const bwKpiTableHeaderClass =
  'bg-[#4472C4] text-white shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.12)]';

export const bwKpiTableHeaderCellClass =
  'sticky top-0 z-20 border-r border-white/25 bg-[#4472C4] last:border-r-0';

/** Stronger divider after the label column — frozen when scrolling horizontally */
export const bwKpiTableHeaderFirstColClass =
  'sticky left-0 top-0 z-30 border-r-2 border-white/45 bg-[#4472C4] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]';

/** First body column — sticky label; pair with `bwKpiTableBodyFirstColClassWithBg()` for opaque bg */
export const bwKpiTableBodyFirstColClass =
  'sticky left-0 z-10 border-r-2 border-indigo-100/90 shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)]';

export type BwStickyFirstColTone = 'odd' | 'even' | 'selected' | 'footer';

/** Opaque backgrounds so horizontally scrolling cells do not show through the frozen column. */
export function bwKpiStickyFirstColBg(tone: BwStickyFirstColTone): string {
  switch (tone) {
    case 'even':
      return 'bg-indigo-50';
    case 'selected':
      return 'bg-indigo-100';
    case 'footer':
      return 'bg-slate-100';
    default:
      return 'bg-white';
  }
}

export function bwKpiStickyFirstColTone(rowIndex: number): BwStickyFirstColTone {
  return rowIndex % 2 === 0 ? 'odd' : 'even';
}

export function bwKpiTableBodyFirstColClassWithBg(
  opts: { rowIndex?: number; selected?: boolean; footer?: boolean; tone?: BwStickyFirstColTone } = {}
): string {
  const { rowIndex = 0, selected = false, footer = false, tone } = opts;
  const resolvedTone =
    tone ?? (footer ? 'footer' : selected ? 'selected' : bwKpiStickyFirstColTone(rowIndex));
  return `${bwKpiTableBodyFirstColClass} ${bwKpiStickyFirstColBg(resolvedTone)}`;
}

export const bwKpiTableZebraOdd = 'bg-white';

export const bwKpiTableZebraEven = 'bg-indigo-50/[0.12]';

/** Selected / active row (cross-filter) */
export const bwKpiTableSelectedRowClass =
  'z-[1] bg-indigo-50/90 ring-2 ring-inset ring-[#4472C4]/35 shadow-[inset_4px_0_0_0_rgb(68,114,196)]';

/** Subtitle strip under chart title (inside KpiChartPanel body) */
export const bwKpiPanelSubtitleClass =
  'border-b border-indigo-100/50 bg-gradient-to-r from-slate-50/50 to-violet-50/25 px-3 py-2 text-xs leading-snug text-slate-600';

export const bwKpiPanelSubtitleClassShrink0 = `${bwKpiPanelSubtitleClass} shrink-0`;

/** Footer / total row on bandwidth KPI tables */
export const bwKpiTableFooterRowClass =
  'border-t-2 border-indigo-100/80 bg-gradient-to-r from-slate-100/95 to-indigo-50/40 font-semibold text-slate-900';

/** Progress bar track (activity / time columns) */
export const bwProgressTrackClass =
  'mt-1.5 h-1.5 w-full max-w-[9rem] overflow-hidden rounded-full bg-violet-100/55 ml-auto';

/** Primary fill — steel blue gradient (KPI-aligned) */
export const bwProgressFillSteelClass = 'h-full rounded-full bg-gradient-to-r from-[#2F5597] to-[#4472C4]';

/** Secondary fill — indigo → violet */
export const bwProgressFillAccentClass = 'h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-600';
